import React, { useEffect, useRef, useState, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../api/http';
import './RecipientsManager.css';

export default function RecipientsManager() {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const saveTimersRef = useRef(new Map()); // id -> timer
  const recipientsRef = useRef(recipients);

  useEffect(() => {
    recipientsRef.current = recipients;
  }, [recipients]);

  const loadRecipients = useCallback(async () => {
    const rows = await apiGet('/api/recipients');
    setRecipients(rows || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await loadRecipients();
      } catch (err) {
        console.error('Failed to load recipients', err);
        alert('Failed to load recipients');
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      for (const t of saveTimersRef.current.values()) clearTimeout(t);
      saveTimersRef.current.clear();
    };
  }, [loadRecipients]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newEmail) {
      alert('Email is required');
      return;
    }
    try {
      await apiPost('/api/recipients', { name: newName, email: newEmail });
      await loadRecipients();
      setNewName('');
      setNewEmail('');
    } catch (err) {
      console.error('Failed to add recipient', err);
      alert('Failed to add recipient');
    }
  };

  const updateLocal = (id, patch) => {
    setRecipients((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const queueSave = (id, debounceMs = 600) => {
    const existing = saveTimersRef.current.get(id);
    if (existing) clearTimeout(existing);

    const t = setTimeout(async () => {
      try {
        const current = recipientsRef.current.find((r) => r.id === id);
        if (!current) return;

        await apiPut(`/api/recipients/${id}`, {
          name: current.name || '',
          is_active: current.is_active ? 1 : 0,
        });
      } catch (err) {
        console.error(`Failed to update recipient ${id}`, err);
        alert('Failed to update recipient');
        try {
          await loadRecipients();
        } catch {}
      } finally {
        saveTimersRef.current.delete(id);
      }
    }, debounceMs);

    saveTimersRef.current.set(id, t);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recipient?')) return;
    try {
      await apiDelete(`/api/recipients/${id}`);
      setRecipients((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error(`Failed to delete recipient ${id}`, err);
      alert('Failed to delete recipient');
    }
  };

  return (
    <div className="card recipients-manager">
      <h2>Summary Email Recipients</h2>

      <form className="editor-form" onSubmit={handleAdd}>
        <div className="form-field">
          <label htmlFor="newName">Name</label>
          <input
            id="newName"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Optional name"
          />
        </div>

        <div className="form-field">
          <label htmlFor="newEmail">Email</label>
          <input
            id="newEmail"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Enter email"
            required
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn primary">Add</button>
        </div>
      </form>

      <hr style={{ margin: '1.5em 0' }} />

      {loading ? (
        <p>Loading recipients...</p>
      ) : recipients.length === 0 ? (
        <p>No recipients added yet.</p>
      ) : (
        <table className="recipients-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {recipients.map((r) => (
              <tr key={r.id}>
                <td data-label="Name">
                  <input
                    type="text"
                    value={r.name || ''}
                    onChange={(e) => {
                      updateLocal(r.id, { name: e.target.value });
                      queueSave(r.id, 650);
                    }}
                    onBlur={() => queueSave(r.id, 50)}
                  />
                </td>

                <td data-label="Email">{r.email}</td>

                <td data-label="Status">
                  <select
                    value={r.is_active ? 1 : 0}
                    onChange={(e) => {
                      updateLocal(r.id, { is_active: Number(e.target.value) });
                      queueSave(r.id, 120);
                    }}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </td>

                <td data-label="Actions">
                  <button className="btn danger" onClick={() => handleDelete(r.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
